<template>
  <basic-layout-dynamic>
    <div class="container">
      <div class="contents">
        <div class="filter">
          <div class="file">
            <div class="file-name">
              <font-awesome-icon
                class="icon icon-with-text"
                icon="file-alt"
              ></font-awesome-icon>
              <a href="#" @click.prevent="$refs.file.click()">
                {{ filename }}
              </a>
            </div>
            <div v-if="fileError" class="file-error">
              {{ fileError }}
            </div>
            <input
              hidden
              ref="file"
              type="file"
              accept=".csv,.txt"
              multiple
              @change="setFiles($event.target.files)"
            />
          </div>
          <div class="actions">
            <div gl-tooltip="変換開始" gl-tooltip-direction="down">
              <gl-button
                primary
                @click="submit"
                :disabled="!canChange"
                icon-pre="search"
                icon-button
              ></gl-button>
            </div>
          </div>
        </div>
        <div class="filter">
          <md-radio v-model="selectMode" value="toCard">
            顧客コード→カードID
          </md-radio>
          <md-radio v-model="selectMode" value="toCustomer">
            カードID→顧客コード
          </md-radio>
        </div>
        <div class="filter">
          <md-checkbox v-model="includeOriginalCode">
            {{ `変換前を含める(${includeOriginalCodeText})` }}
          </md-checkbox>
        </div>
      </div>
      <div class="caution">
        <p>
          カードIDを匿名化してデータ連携している場合はカードIDに変換されません。
        </p>
        <p>
          変換前ファイルと変換後ファイルでは、並び順が一致しない場合があります。
        </p>
        <p>
          カードIDを連携しているかは御社システム管理者にお問い合わせください。
        </p>
      </div>
      <!-- 進捗ダイアログ -->
      <md-dialog
        :md-active.sync="downloading"
        :md-click-outside-to-close="false"
        :md-close-on-esc="false"
      >
        <md-dialog-content>
          <md-steppers
            md-alternative
            md-linear
            md-vertical
            :md-active-step.sync="activeStep"
          >
            <md-step
              id="uploading"
              md-label="Upload"
              :md-editable="false"
              :md-done.sync="completedSteps.uploading"
            >
              <p v-if="!invalidFile">
                ファイルをアップロードしています
                <md-progress-bar md-mode="indeterminate"></md-progress-bar>
              </p>
              <p v-else>
                <span class="warn">一覧ファイルが読み込めませんでした。</span>
              </p>
            </md-step>
            <md-step
              id="calculating"
              md-label="Calculate"
              :md-editable="false"
              :md-done.sync="completedSteps.calculating"
            >
              <p v-if="!noData">
                対象顧客を計算しています
                <md-progress-bar md-mode="indeterminate" />
              </p>
              <p v-else>
                <span class="warn">対象データがありませんでした！</span>
              </p>
            </md-step>
            <md-step
              id="creating"
              md-label="Create"
              :md-editable="false"
              :md-done.sync="completedSteps.creating"
            >
              <p>
                ファイルを作成しています
                <md-progress-bar md-mode="indeterminate"></md-progress-bar>
              </p>
            </md-step>
            <md-step
              id="download-ready"
              md-label="Done!"
              :md-editable="false"
              :md-done.sync="completedSteps.downloadReady"
            >
              <p>
                できた！
                <a href="#" @click.prevent="downloadFile"
                  >ここからダウンロード</a
                >
              </p>
            </md-step>
          </md-steppers>
        </md-dialog-content>
        <md-dialog-actions v-if="canCloseDialog">
          <gl-button @click="downloading = false">Close</gl-button>
        </md-dialog-actions>
      </md-dialog>
    </div>
  </basic-layout-dynamic>
</template>

<script>
/**
 * カード相互変換
 *
 * 顧客コード<->カードIDに変換しダウンロードさせる
 */
export default {
  props: {},
  data() {
    return {
      file: undefined,
      activeStep: 'uploading',
      completedSteps: {
        uploading: false,
        calculating: false,
        creating: false,
        downloadReady: false,
      },
      includeOriginalCode: false,

      downloading: false,
      downloadLink: undefined,
      noData: false,
      invalidFile: false,
      selectMode: 'toCard',
      fileError: undefined,
    }
  },
  computed: {
    filename() {
      return this.file ? this.file.name : 'ファイルを選択してください'
    },
    canChange() {
      if (!this.file) {
        return false
      }
      return true
    },
    canDownload() {
      if (!this.file) {
        return false
      }
      if (this.downloading) {
        return false
      }

      return true
    },
    canCloseDialog() {
      return (
        this.activeStep === 'download-ready' || this.invalidFile || this.noData
      )
    },
    includeOriginalCodeText() {
      return this.selectMode === 'toCard'
        ? '1列目:変換後のカードID / 2列目:変換前の顧客コード'
        : '1列目:変換後の顧客コード / 2列目:変換前のカードID'
    },
  },
  methods: {
    async setFiles(files) {
      if (files.length === 0) {
        return
      }
      const allowed = ['csv', 'txt']
      const isValid = Array.from(files).every(file => {
        const ext = file.name
          .split('.')
          .pop()
          .toLowerCase()
        return allowed.includes(ext)
      })
      if (isValid) {
        this.fileError = undefined
        this.file = files[0]
      } else {
        this.fileError = 'CSVまたはTXTファイルを選択してください'
        this.file = undefined
        this.$refs.file.value = null
      }
    },
    async submit() {
      this.downloading = true
      this.invalidFile = false
      this.noData = false
      this.completedSteps.uploading = false
      this.completedSteps.calculating = false
      this.completedSteps.creating = false
      this.completedSteps.downloadReady = false
      this.activeStep = 'uploading'
      const repo = this.$repositories.get('uploadFile')

      // Upload
      const { url, id } = await repo.issueUploadUrl(this.file.name)
      await repo.upload(url, this.file)
      this.completedSteps.uploading = true

      // 変換
      this.activeStep = 'calculating'
      let result
      if (this.selectMode === 'toCard') {
        result = await repo.loadCardFile(id, this.includeOriginalCode)
      } else {
        result = await repo.loadCustomerFile(id, this.includeOriginalCode)
      }

      if (result.numRows === 0) {
        this.noData = true
        return
      }
      this.completedSteps.calculating = true
      this.activeStep = 'creating'

      // ファイル化
      this.downloadLink = await this.$customerGroup.archive(
        result.customerGroupId,
      )

      this.completedSteps.creating = true
      this.completedSteps.downloadReady = true
      this.activeStep = 'download-ready'
    },
    downloadFile() {
      window.open(this.downloadLink)
    },
  },
  mounted() {},
}
</script>
<style lang="scss" scoped>
.container {
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
}
.filter {
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: row;
}
.actions {
  align-items: center;
  display: flex;
  flex-direction: row;
  padding-left: var(--spacing-large);
}
.contents {
  width: auto;
  max-width: 1000px;
  padding-top: var(--spacing-large);
  padding-bottom: var(--spacing-large);
}
.file-error {
  color: red;
  font-size: 0.8rem;
  margin-top: var(--spacing-small);
}
.file-name {
  font-size: 1.3rem;

  .icon {
    color: #00b0ff;
    margin-right: var(--spacing-small);
  }
}
.md-table-row {
  .customer-count {
    text-align: right;
  }
}
</style>
